import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { firebaseConfig } from '../../util/FirebaseConfig';

/**
 * Firebase Feedback Service
 * 
 * Handles feedback operations using Firebase Firestore:
 * - Feedback submission
 * - Feedback retrieval
 * - Error handling
 * 
 * @class FeedbackFirebaseService
 */
class FeedbackFirebaseService {
  constructor() {
    // Initialize Firebase if not already initialized
    if (!firebase.apps.length) {
      firebase.initializeApp(firebaseConfig);
    }
    this.db = firebase.firestore();
  }

  /**
   * Submits user feedback to Firestore
   * @param {Object} feedbackData - Feedback data
   * @param {string} feedbackData.name - User's name
   * @param {string} feedbackData.email - User's email
   * @param {string} feedbackData.feedback - Feedback content
   * @param {Date} feedbackData.dateTime - Submission date/time
   * @returns {Promise<Object>} Submission result with document ID
   */
  async submitFeedback(feedbackData) {
    try {
      const feedbackEntry = {
        name: feedbackData.name,
        email: feedbackData.email,
        feedback: feedbackData.feedback,
        dateTime: feedbackData.dateTime || new Date(),
        timestamp: firebase.firestore.FieldValue.serverTimestamp(),
        status: 'submitted', // Track feedback status
      };

      const docRef = await this.db.collection('feedback').add(feedbackEntry);
      
      return {
        success: true,
        id: docRef.id,
        message: 'Feedback submitted successfully'
      };
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw new Error('Failed to submit feedback. Please try again.');
    }
  }

  /**
   * Gets user's feedback history
   * @param {string} userEmail - User's email to filter feedback
   * @param {number} limit - Maximum number of feedback items to retrieve
   * @returns {Promise<Array>} List of user's feedback submissions
   */
  async getUserFeedbackHistory(userEmail, limit = 10) {
    try {
      const querySnapshot = await this.db
        .collection('feedback')
        .where('email', '==', userEmail)
        .orderBy('dateTime', 'desc')
        .limit(limit)
        .get();

      const feedbackList = [];
      querySnapshot.forEach((doc) => {
        feedbackList.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return feedbackList;
    } catch (error) {
      console.error('Error fetching feedback history:', error);
      throw new Error('Failed to fetch feedback history');
    }
  }

  /**
   * Gets all feedback (admin function)
   * @param {Object} options - Query options
   * @param {number} options.limit - Maximum number of items to retrieve
   * @param {string} options.orderBy - Field to order by
   * @param {string} options.order - Order direction ('asc' or 'desc')
   * @returns {Promise<Array>} List of all feedback submissions
   */
  async getAllFeedback(options = {}) {
    try {
      const {
        limit = 50,
        orderBy = 'dateTime',
        order = 'desc'
      } = options;

      const querySnapshot = await this.db
        .collection('feedback')
        .orderBy(orderBy, order)
        .limit(limit)
        .get();

      const feedbackList = [];
      querySnapshot.forEach((doc) => {
        feedbackList.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return feedbackList;
    } catch (error) {
      console.error('Error fetching all feedback:', error);
      throw new Error('Failed to fetch feedback data');
    }
  }

  /**
   * Updates feedback status
   * @param {string} feedbackId - Feedback document ID
   * @param {string} status - New status ('reviewed', 'resolved', 'archived')
   * @returns {Promise<Object>} Update result
   */
  async updateFeedbackStatus(feedbackId, status) {
    try {
      await this.db.collection('feedback').doc(feedbackId).update({
        status,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      });

      return {
        success: true,
        message: 'Feedback status updated successfully'
      };
    } catch (error) {
      console.error('Error updating feedback status:', error);
      throw new Error('Failed to update feedback status');
    }
  }

  /**
   * Deletes feedback
   * @param {string} feedbackId - Feedback document ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFeedback(feedbackId) {
    try {
      await this.db.collection('feedback').doc(feedbackId).delete();
      
      return {
        success: true,
        message: 'Feedback deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting feedback:', error);
      throw new Error('Failed to delete feedback');
    }
  }

  /**
   * Gets feedback statistics
   * @returns {Promise<Object>} Feedback analytics data
   */
  async getFeedbackStats() {
    try {
      const snapshot = await this.db.collection('feedback').get();
      const totalFeedback = snapshot.size;

      // Count by status
      const statusCounts = {};
      snapshot.forEach((doc) => {
        const status = doc.data().status || 'submitted';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });

      return {
        total: totalFeedback,
        statusBreakdown: statusCounts,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching feedback stats:', error);
      throw new Error('Failed to fetch feedback statistics');
    }
  }

  /**
   * Validates feedback data before submission
   * @param {Object} feedbackData - Feedback data to validate
   * @returns {Object} Validation result
   */
  validateFeedbackData(feedbackData) {
    const errors = [];

    if (!feedbackData.name || feedbackData.name.trim().length === 0) {
      errors.push('Name is required');
    }

    if (!feedbackData.email || feedbackData.email.trim().length === 0) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(feedbackData.email)) {
      errors.push('Email format is invalid');
    }

    if (!feedbackData.feedback || feedbackData.feedback.trim().length === 0) {
      errors.push('Feedback content is required');
    } else if (feedbackData.feedback.trim().length < 10) {
      errors.push('Feedback must be at least 10 characters long');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Create and export singleton instance
const feedbackFirebaseService = new FeedbackFirebaseService();
export default feedbackFirebaseService; 